import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import i18next from '../../locales/i18n';
import '../ui/bnb-logo';
import '../ui/language-switcher';

@customElement('bnb-header')
export class BnbHeader extends LitElement {
  static styles = css`
    :host {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 200;
      transition: all 0.3s ease;
      background: transparent;
      backdrop-filter: none;
      border-bottom: none;
    }

    :host(.scrolled) {
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(10px);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      border-bottom: 1px solid rgba(229, 231, 235, 0.8);
    }

    .header {
      max-width: 1280px;
      margin: 0 auto;
      padding: 1rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: relative;
    }

    .logo-container {
      flex-shrink: 0;
    }

    .navigation {
      display: none;
      gap: 2rem;
      list-style: none;
      margin: 0;
      padding: 0;
    }

    .navigation a {
      text-decoration: none;
      color: white;
      font-weight: 400;
      font-size: 0.9rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      transition: all 0.25s ease;
      position: relative;
    }

    :host(.scrolled) .navigation a {
      color: #374151;
    }

    .navigation a:hover {
      color: #00bcd4;
    }

    :host(.scrolled) .navigation a:hover {
      color: #00bcd4;
    }

    .navigation a::after {
      content: '';
      position: absolute;
      bottom: -4px;
      left: 0;
      width: 0;
      height: 2px;
      background: #00bcd4;
      transition: width 0.25s ease;
    }

    .navigation a:hover::after {
      width: 100%;
    }

    .navigation a:focus-visible {
      outline: 3px solid #00bcd4;
      outline-offset: 4px;
      border-radius: 2px;
    }

    .mobile-menu-toggle {
      display: flex;
      flex-direction: column;
      gap: 3px;
      background: none;
      border: none;
      padding: 12px;
      cursor: pointer;
      border-radius: 4px;
      transition: background-color 0.2s ease;
      min-width: 44px;
      min-height: 44px;
      align-items: center;
      justify-content: center;
    }

    .mobile-menu-toggle:hover {
      background-color: rgba(107, 114, 128, 0.1);
    }

    .mobile-menu-toggle:focus-visible {
      outline: 3px solid #00bcd4;
      outline-offset: 2px;
    }

    .mobile-menu-toggle span {
      width: 20px;
      height: 2px;
      background: white;
      transition: all 0.3s ease;
      transform-origin: center;
    }

    :host(.scrolled) .mobile-menu-toggle span {
      background: #374151;
    }

    .mobile-menu-toggle.open span:nth-child(1) {
      transform: translateY(5px) rotate(45deg);
    }

    .mobile-menu-toggle.open span:nth-child(2) {
      opacity: 0;
    }

    .mobile-menu-toggle.open span:nth-child(3) {
      transform: translateY(-5px) rotate(-45deg);
    }

    .mobile-menu {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: white;
      border-bottom: 1px solid #e5e7eb;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      transform-origin: top;
      transform: scaleY(0);
      opacity: 0;
      visibility: hidden;
      transition: all 0.3s ease;
    }

    .mobile-menu.open {
      transform: scaleY(1);
      opacity: 1;
      visibility: visible;
    }

    .mobile-menu ul {
      list-style: none;
      margin: 0;
      padding: 1rem;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .mobile-menu a {
      text-decoration: none;
      color: #374151;
      font-weight: 300;
      font-size: 1.125rem;
      padding: 0.75rem;
      border-radius: 6px;
      transition: background-color 0.2s ease;
      display: block;
    }

    .mobile-menu a:hover {
      background-color: rgba(37, 99, 235, 0.1);
      color: #2563eb;
    }

    .mobile-menu a:focus-visible {
      outline: 3px solid #00bcd4;
      outline-offset: 2px;
    }

    /* Desktop navigation */
    @media (min-width: 1024px) {
      .navigation {
        display: flex;
      }

      .mobile-menu-toggle {
        display: none;
      }

      .mobile-menu {
        display: none;
      }
    }

    /* Logo size adjustments for header */
    bnb-logo {
      --logo-height: 40px;
    }

    :host(.scrolled) bnb-logo {
      --logo-height: 32px;
    }

    /* Language switcher positioning */
    .header-right {
      display: flex;
      align-items: center;
      gap: 2rem;
    }

    language-switcher {
      flex-shrink: 0;
    }

    :host(.scrolled) language-switcher {
      /* Pass scrolled state to language switcher */
    }

    @media (min-width: 1024px) {
      .header-right {
        display: flex;
      }
    }
  `;

  @property({ type: String })
  title = 'BrandNewBroadcast';

  @state()
  private navigation = this.getNavigation();

  @property({ type: Boolean })
  'mobile-menu-open' = false;

  private boundHandleKeydown = this.handleKeydown.bind(this);
  private boundHandleLanguageChange = this.handleLanguageChange.bind(this);

  private handleLanguageChange() {
    this.navigation = this.getNavigation();
    this.requestUpdate();
  }

  private getNavigation() {
    return [
      { text: i18next.t('nav.whatWeDo'), href: '#what-we-do' },
      { text: i18next.t('nav.about'), href: '#about' },
      { text: i18next.t('nav.contact'), href: '#contact' },
      { text: i18next.t('nav.partners'), href: '#partners' }
    ];
  }

  private toggleMobileMenu() {
    this['mobile-menu-open'] = !this['mobile-menu-open'];
    this.requestUpdate();
  }

  private handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && this['mobile-menu-open']) {
      this['mobile-menu-open'] = false;
      this.requestUpdate();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('keydown', this.boundHandleKeydown);

    // Listen for language changes and update navigation
    i18next.on('languageChanged', this.boundHandleLanguageChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('keydown', this.boundHandleKeydown);
    i18next.off('languageChanged', this.boundHandleLanguageChange);
  }

  private handleNavigationClick(e: Event, href: string) {
    e.preventDefault();

    // Close mobile menu when navigation item is clicked
    this['mobile-menu-open'] = false;
    this.requestUpdate();

    // Find element in bnb-app Shadow DOM
    const bnbApp = document.querySelector('bnb-app');
    const targetElement = bnbApp?.shadowRoot?.querySelector(href);

    if (targetElement) {
      const offset = 80;
      const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - offset;

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  }

  render() {
    return html`
      <div class="header">
        <div class="logo-container">
          <bnb-logo
            size="md"
            variant="color"
            href="#"
            title="${this.title}">
          </bnb-logo>
        </div>

        <div class="header-right">
          <nav class="navigation" role="navigation" aria-label="${i18next.t('aria.mainNav')}">
            ${this.navigation.map((item, index) => html`
              <a
                href="${item.href}"
                @click="${(e: Event) => this.handleNavigationClick(e, item.href)}"
                aria-current="${index === 0 ? 'page' : 'false'}"
              >
                ${item.text}
              </a>
            `)}
          </nav>

          <language-switcher class="${this.classList.contains('scrolled') ? 'scrolled' : ''}"></language-switcher>
        </div>

        <button
          class="mobile-menu-toggle ${this['mobile-menu-open'] ? 'open' : ''}"
          @click="${this.toggleMobileMenu}"
          aria-label="${i18next.t('aria.menuToggle')}"
          aria-expanded="${this['mobile-menu-open']}"
          aria-controls="mobile-menu"
        >
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
        </button>

        <nav class="mobile-menu ${this['mobile-menu-open'] ? 'open' : ''}" id="mobile-menu" role="navigation" aria-label="${i18next.t('aria.mobileMenu')}">
          <ul>
            ${this.navigation.map(item => html`
              <li>
                <a
                  href="${item.href}"
                  @click="${(e: Event) => this.handleNavigationClick(e, item.href)}"
                >
                  ${item.text}
                </a>
              </li>
            `)}
          </ul>
        </nav>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bnb-header': BnbHeader;
  }
}